import React, { useState, useEffect } from 'react';
import { Activity, Copy, Check, Info } from 'lucide-react';

type UnitSystem = 'metric' | 'imperial';

interface BMICategory {
  range: string;
  label: string;
  color: string;
  bgColor: string;
}

const bmiCategories: BMICategory[] = [
  { range: '< 18.5', label: 'Underweight', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  { range: '18.5 - 24.9', label: 'Normal', color: 'text-green-600', bgColor: 'bg-green-100' },
  { range: '25 - 29.9', label: 'Overweight', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  { range: '30 - 34.9', label: 'Obese I', color: 'text-orange-600', bgColor: 'bg-orange-100' },
  { range: '35 - 39.9', label: 'Obese II', color: 'text-red-500', bgColor: 'bg-red-100' },
  { range: '≥ 40', label: 'Obese III', color: 'text-red-700', bgColor: 'bg-red-200' },
];

const BMICalculator: React.FC = () => {
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('metric');
  const [weight, setWeight] = useState('70');
  const [heightCm, setHeightCm] = useState('175');
  const [heightFt, setHeightFt] = useState('5');
  const [heightIn, setHeightIn] = useState('9');
  const [weightLbs, setWeightLbs] = useState('154');
  const [bmi, setBmi] = useState('');
  const [category, setCategory] = useState<BMICategory | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitSystem, weight, heightCm, heightFt, heightIn, weightLbs]);

  const calculate = () => {
    let weightKg: number;
    let heightM: number;

    if (unitSystem === 'metric') {
      weightKg = parseFloat(weight);
      heightM = parseFloat(heightCm) / 100;
    } else {
      weightKg = parseFloat(weightLbs) * 0.453592;
      const totalInches = parseFloat(heightFt) * 12 + parseFloat(heightIn);
      heightM = totalInches * 0.0254;
    }

    if (isNaN(weightKg) || isNaN(heightM) || weightKg <= 0 || heightM <= 0) {
      setBmi('');
      setCategory(null);
      return;
    }

    const bmiValue = weightKg / (heightM * heightM);
    setBmi(bmiValue.toFixed(1));

    if (bmiValue < 18.5) setCategory(bmiCategories[0]);
    else if (bmiValue < 25) setCategory(bmiCategories[1]);
    else if (bmiValue < 30) setCategory(bmiCategories[2]);
    else if (bmiValue < 35) setCategory(bmiCategories[3]);
    else if (bmiValue < 40) setCategory(bmiCategories[4]);
    else setCategory(bmiCategories[5]);
  };

  const copyResult = async () => {
    if (!bmi) return;
    await navigator.clipboard.writeText(bmi);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getBmiPosition = () => {
    const bmiValue = parseFloat(bmi);
    if (isNaN(bmiValue)) return 0;
    const clamped = Math.min(Math.max(bmiValue, 15), 45);
    return ((clamped - 15) / 30) * 100;
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">BMI Calculator</h2>
        <p className="text-gray-600">Calculate your Body Mass Index</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex justify-center gap-2 p-1 bg-gray-100 rounded-lg w-fit mx-auto">
          <button
            onClick={() => setUnitSystem('metric')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              unitSystem === 'metric' ? 'bg-white text-purple-600 shadow' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Metric (kg/cm)
          </button>
          <button
            onClick={() => setUnitSystem('imperial')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              unitSystem === 'imperial' ? 'bg-white text-purple-600 shadow' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Imperial (lbs/ft)
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          {unitSystem === 'metric' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Weight (kg)</label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="70"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)</label>
                <input
                  type="number"
                  value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)}
                  className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="175"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Weight (lbs)</label>
                <input
                  type="number"
                  value={weightLbs}
                  onChange={(e) => setWeightLbs(e.target.value)}
                  className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="154"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="number"
                      value={heightFt}
                      onChange={(e) => setHeightFt(e.target.value)}
                      className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="5"
                    />
                    <span className="text-sm text-gray-500 mt-1 block">feet</span>
                  </div>
                  <div>
                    <input
                      type="number"
                      value={heightIn}
                      onChange={(e) => setHeightIn(e.target.value)}
                      className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="9"
                    />
                    <span className="text-sm text-gray-500 mt-1 block">inches</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {bmi && category && (
          <div className={`rounded-xl border p-6 ${category.bgColor} border-opacity-50`}>
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-2">
                <p className="text-4xl font-bold text-gray-900">{bmi}</p>
                <button onClick={copyResult} className="p-1 text-gray-400 hover:text-gray-600">
                  {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
              <p className={`text-lg font-semibold ${category.color}`}>{category.label}</p>
            </div>

            <div className="relative h-4 bg-gradient-to-r from-blue-400 via-green-400 via-yellow-400 via-orange-400 to-red-500 rounded-full">
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-gray-800 rounded-full shadow-lg"
                style={{ left: `calc(${getBmiPosition()}% - 8px)` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>15</span>
              <span>18.5</span>
              <span>25</span>
              <span>30</span>
              <span>35</span>
              <span>40+</span>
            </div>
          </div>
        )}

        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            BMI Categories
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {bmiCategories.map((cat) => (
              <div key={cat.label} className={`p-2 rounded-lg ${cat.bgColor}`}>
                <p className={`font-medium ${cat.color}`}>{cat.label}</p>
                <p className="text-sm text-gray-600">{cat.range}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <p className="text-sm text-blue-700">
              BMI is a screening tool and doesn't directly measure body fat. 
              Athletes may have a high BMI due to muscle mass. Consult a healthcare provider for a complete assessment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BMICalculator;
